/**
 * Filtrare l'elenco dei generi scrivendo, sul telefono.
 *
 * ## Perché esiste qui e non sul televisore
 *
 * Quaranta generi su uno schermo da 390 px sono quattro schermate di scorrimento col pollice, e
 * scrivere tre lettere è immediato: c'è una tastiera vera. Sul televisore lo stesso campo sarebbe
 * una **punizione** — si scrive con le frecce su una tastiera a schermo, e tre lettere costano venti
 * pressioni (`docs/04-SCHERMATE-MOBILE.md` §5).
 *
 * È lo stesso dato con due grammatiche d'ingresso diverse, che è il tema di tutto il telefono.
 *
 * ## Si cerca **dentro**, non dall'inizio
 *
 * Chi scrive «azione» non sta completando un nome: sta restringendo un elenco. Con un confronto a
 * prefisso, «commedia» non si troverebbe scrivendo «media», e «Film d'azione» non si troverebbe
 * scrivendo «azione» — che è esattamente quel che chiunque digiterebbe.
 *
 * ## Accenti e maiuscole non contano
 *
 * Un elenco di generi arriva dal fornitore e non è normalizzato: `Commedia`, `COMMEDIA`, `Drammatico`
 * e `Dramático` convivono. Chi scrive su una tastiera di telefono non mette gli accenti e non pensa
 * alle maiuscole, e un filtro che glieli chiede sembra un elenco vuoto — cioè un catalogo senza quel
 * genere.
 */

/**
 * La forma su cui si confronta: minuscola e senza segni diacritici.
 *
 * `̀-ͯ` è un intervallo di caratteri, **non** una proprietà Unicode: `\p{M}` o `\p{L}`
 * sarebbero più espressivi e sono **vietati** — su Tizen 5 un'espressione regolare con `\p{…}` è un
 * `SyntaxError` alla lettura del modulo, cioè un'app che non parte affatto, e non lo vedono né il
 * compilatore né le prove che girano qui.
 */
function comparable(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * I generi che restano scrivendo `query`.
 *
 * ## Una ricerca vuota risponde tutto, e oggi lo fa da sé
 *
 * È lo stato di apertura della schermata. `''` è contenuto in qualunque stringa, quindi con questo
 * confronto il caso si risolve senza un ramo dedicato — e un ramo dedicato ci **era**, tolto perché
 * non poteva mai cambiare l'esito: la mutazione che lo elimina non faceva fallire niente.
 *
 * La prova che lo verifica resta, e non è vacua: non difende questa riga, difende il
 * **comportamento**. Il giorno che il confronto diventasse più furbo — parole intere, iniziali —
 * l'elenco si svuoterebbe all'apertura, prima che qualcuno abbia scritto, e si leggerebbe come un
 * catalogo senza generi.
 *
 * ## L'ordine di partenza non si tocca
 *
 * Niente riordino per pertinenza. L'elenco dei generi ha un ordine che qualcuno ha deciso — alfabetico
 * o di catalogo — e vederlo rimescolare a ogni lettera digitata è disorientante: le voci ballano
 * sotto il dito mentre si scrive.
 */
export function filterGenres<T extends { readonly name: string }>(
  genres: ReadonlyArray<T>,
  query: string,
): T[] {
  const needle = comparable(query);
  return genres.filter((genre) => comparable(genre.name).includes(needle));
}
